/**
 * #40 · Órdenes de etiquetas.
 *
 * Comprar etiquetas y fabricarlas son dos actos distintos: la orden nace en
 * `draft` sin reservar un solo id, y entre el alta y la generación el cliente
 * todavía puede cancelar. Por eso el alta no ofrece "crear y generar": el
 * botón de generar vive en el detalle, detrás de su propia confirmación.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useKeysetQuery } from '@/api/hooks';
import { labelOrderKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useAuth } from '@/auth/useAuth';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { Dialog } from '../products/ui/Dialog';
import { SelectField } from '../products/ui/fields';
import {
  ORDER_STATUSES,
  ORDER_STATUS_LABEL_KEYS,
  ORDER_STATUS_TONES,
  isOrderStatus,
  serialRange,
} from './custody';
import { LabelOrderForm, type LabelOrderDraft } from './LabelOrderForm';
import { LabelsTabs } from './LabelsTabs';

type LabelOrder = Schemas['LabelOrderOut'];

const PAGE_SIZE = 50;

export function LabelOrdersPage(): ReactNode {
  const { t, fmt } = useI18n();
  const { can } = useAuth();
  const api = useApi();
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  const rawStatus = searchParams.get('status');
  const status = rawStatus !== null && isOrderStatus(rawStatus) ? rawStatus : null;

  const [creating, setCreating] = useState(false);

  const orders = useKeysetQuery<LabelOrder>(
    labelOrderKeys.list({ limit: PAGE_SIZE, status }),
    '/api/v1/label-orders',
    { status },
    { limit: PAGE_SIZE },
  );
  const { query } = orders;

  const create = useMutation<LabelOrder, unknown, LabelOrderDraft>({
    mutationFn: (draft) => api.post<LabelOrder>('/api/v1/label-orders', draft),
    onSuccess: () => {
      setCreating(false);
      void queryClient.invalidateQueries({ queryKey: labelOrderKeys.all });
    },
  });

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <LabelsTabs active="orders" />

      <Card>
        <CardHeader
          title={t('labels.orders.title')}
          description={t('labels.orders.description')}
          actions={
            <>
              {orders.items.length > 0 && (
                <Badge tone="neutral">
                  {t('labels.orders.count', { count: orders.items.length })}
                </Badge>
              )}
              {can('admin') && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    create.reset();
                    setCreating(true);
                  }}
                >
                  {t('labels.orders.new')}
                </Button>
              )}
            </>
          }
        />

        <CardBody className="border-b border-border">
          <SelectField
            label={t('labels.orders.filter.status')}
            value={status ?? ''}
            onChange={(value) => {
              const next = new URLSearchParams(searchParams);
              if (value === '') next.delete('status');
              else next.set('status', value);
              setSearchParams(next, { replace: true });
            }}
            options={[
              { value: '', label: t('labels.orders.filter.all') },
              ...ORDER_STATUSES.map((value) => ({
                value,
                label: t(ORDER_STATUS_LABEL_KEYS[value]),
              })),
            ]}
          />
        </CardBody>

        {query.isPending && <LoadingState rows={6} />}

        {query.isError && (
          <CardBody>
            <ErrorState
              error={query.error}
              onRetry={() => {
                void query.refetch();
              }}
            />
          </CardBody>
        )}

        {!query.isPending && !query.isError && orders.items.length === 0 && (
          <EmptyState title={t('labels.orders.empty')} detail={t('labels.orders.empty.detail')} />
        )}

        {orders.items.length > 0 && (
          <>
            <Table caption={t('labels.orders.description')}>
              <thead>
                <tr>
                  <Th>{t('labels.orders.column.code')}</Th>
                  <Th>{t('labels.orders.column.supplier')}</Th>
                  <Th className="text-right">{t('labels.orders.column.quantity')}</Th>
                  <Th className="text-right">{t('labels.orders.column.rolls')}</Th>
                  <Th>{t('labels.orders.column.serials')}</Th>
                  <Th>{t('labels.orders.column.status')}</Th>
                  <Th>{t('labels.orders.column.generated')}</Th>
                </tr>
              </thead>
              <tbody>
                {orders.items.map((order) => (
                  <tr key={order.id}>
                    <Td>
                      <Link
                        to={`/labels/orders/${String(order.id)}`}
                        className="font-mono font-medium text-accent underline underline-offset-2"
                      >
                        {order.order_code}
                      </Link>
                    </Td>
                    <Td className="text-ink-muted">{order.supplier}</Td>
                    <Td className="text-right tabular-nums">{fmt.number(order.quantity)}</Td>
                    <Td className="text-right tabular-nums">{fmt.number(order.roll_count)}</Td>
                    <Td className="font-mono text-xs text-ink-muted">
                      {serialRange(order.serial_from, order.serial_to, (value) =>
                        fmt.number(value),
                      ) ?? '—'}
                    </Td>
                    <Td>
                      <Badge
                        tone={
                          isOrderStatus(order.status) ? ORDER_STATUS_TONES[order.status] : 'neutral'
                        }
                      >
                        {isOrderStatus(order.status)
                          ? t(ORDER_STATUS_LABEL_KEYS[order.status])
                          : order.status}
                      </Badge>
                    </Td>
                    <Td className="text-ink-muted">
                      {order.generated_at === null || order.generated_at === undefined
                        ? '—'
                        : fmt.dateTime(order.generated_at)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <CardBody className="flex items-center justify-center border-t border-border">
              {orders.hasMore ? (
                <Button size="sm" onClick={orders.loadMore} disabled={orders.isLoadingMore}>
                  {orders.isLoadingMore ? t('state.loadingMore') : t('state.loadMore')}
                </Button>
              ) : (
                <p className="text-xs text-ink-subtle">{t('state.allLoaded')}</p>
              )}
            </CardBody>
          </>
        )}
      </Card>

      <Dialog
        open={creating}
        onClose={() => {
          setCreating(false);
        }}
        title={t('labels.orders.new.title')}
        description={t('labels.orders.new.description')}
      >
        <LabelOrderForm
          onSubmit={(draft) => {
            create.mutate(draft);
          }}
          onCancel={() => {
            setCreating(false);
          }}
          isSubmitting={create.isPending}
          error={create.error}
        />
      </Dialog>
    </div>
  );
}
